import { create } from "zustand";
import type { WsServerEvent, LaunchResponse, OpenRoundResponse, ResolveRoundResponse } from "@vb/rgs-contract";
import { playLockCrack } from "./lib/sound.js";
import { rgs } from "./lib/rgs-client.js";
import {
  safeGetBool,
  safeGetJson,
  safeSetBool,
  safeSetJson,
} from "./lib/persist.js";
import { applyStrategy, type Strategy } from "./lib/autoplay-strategies.js";

export type Phase = "idle" | "betting" | "live" | "win" | "bust";

export interface HistoryEntry {
  multiplier: number;
  roundId: string | null;
  ts: number;
}

export interface AutoplayConfig {
  strategy: Strategy;
  /** 0 = infinite. */
  numberOfBets: number;
  baseBet: number;
  autoCashoutTarget: number;
  onWinPct: number;
  onLossPct: number;
  stopOnProfit: number | null;
  stopOnLoss: number | null;
  /** When true, the win/bust cinematic is skipped during autoplay so the loop
   *  flies through rounds at server tempo instead of the 9.5s/4.5s cinematic
   *  budget. */
  skipAnimations: boolean;
}

export interface AutoplayRuntime {
  active: boolean;
  paused: boolean;
  /** Set true while a /rgs/round/resolve POST is in flight. Blocks duplicate auto-cashout. */
  cashoutInFlight: boolean;
  /** Tick handler sets this; GameView observes and dispatches the cashout call. */
  pendingAutoCashout: boolean;
  remaining: number;
  currentBet: number;
  cumulativeProfit: number;
  fibIndex: number;
  unitCount: number;
  startedAt: number | null;
  stopReason: string | null;
}

const DEFAULT_AUTOPLAY_CONFIG: AutoplayConfig = {
  strategy: "manual",
  numberOfBets: 10,
  baseBet: 0.001,
  autoCashoutTarget: 1.5,
  onWinPct: 0,
  onLossPct: 0,
  stopOnProfit: null,
  stopOnLoss: null,
  skipAnimations: false,
};

const DEFAULT_AUTOPLAY_RUNTIME: AutoplayRuntime = {
  active: false,
  paused: false,
  cashoutInFlight: false,
  pendingAutoCashout: false,
  remaining: 0,
  currentBet: 0,
  cumulativeProfit: 0,
  fibIndex: 0,
  unitCount: 0,
  startedAt: null,
  stopReason: null,
};

const PERSIST_KEY_AUTOPLAY = "vb:autoplay-config";
const PERSIST_KEY_MUTED = "vb:muted";
const PERSIST_KEY_HIDE_BALANCE = "vb:hide-balance";
const PERSIST_KEY_FAIRNESS_OVERLAY = "vb:fairness-overlay";

export interface GameState {
  // session
  sessionId: string | null;
  token: string | null;
  currency: "BTC" | "ETH" | "SOL" | "USDT" | "USD";
  /** Which game this session is bound to. Set by the bootstrap call in each
   *  game's component (`vault-breaker` for /play, `pressure-plate` for
   *  /play/pressure-plate, etc). The store stays single-tenant per page; if
   *  a player navigates between games the store re-hydrates from a fresh
   *  /rgs/session/launch with the new gameSlug. */
  gameSlug: "vault-breaker" | "pressure-plate" | "cipher-crack";
  balance: number;
  serverSeedHash: string | null;
  clientSeed: string | null;

  // round
  phase: Phase;
  roundId: string | null;
  roundNonce: number | null;
  betAmount: number;
  multiplier: number;
  cracked: number;
  cashoutMultiplier: number | null;
  payout: number;

  // ui
  history: HistoryEntry[];
  crackPulseKey: number;
  operatorId: string;
  locale: string;
  demoMode: boolean;
  muted: boolean;
  hideBalance: boolean;
  fairnessOverlay: boolean;

  // autoplay
  autoplay: AutoplayRuntime;
  autoplayConfig: AutoplayConfig;

  // actions
  hydrateFromLaunch: (res: LaunchResponse) => void;
  applyOpenRound: (res: OpenRoundResponse) => void;
  applyResolved: (res: ResolveRoundResponse) => void;
  applyWsEvent: (ev: WsServerEvent) => void;
  setBet: (v: number) => void;
  setPhase: (p: Phase) => void;
  reset: () => void;
  /** Wipe session-bound state. Call before re-launching with a new gameSlug. */
  clearSession: () => void;
  /** Re-launch the session with the same operator/currency to refill the
   *  demo wallet when the player has run the balance to zero. Each game
   *  passes its own slug so the new session matches the current page. */
  refillDemoBalance: (gameSlug: GameState["gameSlug"]) => Promise<void>;
  setOperator: (operatorId: string, locale: string) => void;
  setBalance: (n: number) => void;
  setDemoMode: (on: boolean) => void;

  // toggles
  setMuted: (v: boolean) => void;
  setHideBalance: (v: boolean) => void;
  setFairnessOverlay: (v: boolean) => void;

  // autoplay actions
  setAutoplayConfig: (patch: Partial<AutoplayConfig>) => void;
  startAutoplay: () => { ok: boolean; reason?: string };
  stopAutoplay: (reason?: string) => void;
  pauseAutoplay: () => void;
  resumeAutoplay: () => void;
  setCashoutInFlight: (v: boolean) => void;
  acknowledgeAutoCashout: () => void;
  /**
   * Apply the strategy + run the limit checks after a round resolves.
   * Returns the bet amount for the next round, or null if autoplay should stop.
   */
  resolveAutoplay: (outcome: "win" | "bust", payoutDelta: number) => number | null;
}

function pushHistory(prev: HistoryEntry[], entry: HistoryEntry): HistoryEntry[] {
  return [entry, ...prev].slice(0, 12);
}

export const useGame = create<GameState>((set, get) => ({
  sessionId: null,
  token: null,
  currency: "BTC",
  gameSlug: "vault-breaker",
  balance: 0,
  serverSeedHash: null,
  clientSeed: null,

  phase: "idle",
  roundId: null,
  roundNonce: null,
  betAmount: 0.001,
  multiplier: 1,
  cracked: 0,
  cashoutMultiplier: null,
  payout: 0,

  history: [],
  crackPulseKey: 0,
  operatorId: "demo",
  locale: "en",
  demoMode: false,
  muted: safeGetBool(PERSIST_KEY_MUTED),
  hideBalance: safeGetBool(PERSIST_KEY_HIDE_BALANCE),
  fairnessOverlay: safeGetBool(PERSIST_KEY_FAIRNESS_OVERLAY),

  autoplay: DEFAULT_AUTOPLAY_RUNTIME,
  autoplayConfig: safeGetJson<AutoplayConfig>(PERSIST_KEY_AUTOPLAY, DEFAULT_AUTOPLAY_CONFIG),

  hydrateFromLaunch: (res) =>
    set({
      sessionId: res.sessionId,
      token: res.token,
      currency: res.currency,
      gameSlug: res.gameSlug,
      balance: res.balance,
      serverSeedHash: res.serverSeedHash,
      clientSeed: res.clientSeed,
      phase: "betting",
    }),

  applyOpenRound: (res) =>
    set({
      roundId: res.roundId,
      roundNonce: res.nonce,
      balance: res.balance,
      multiplier: 1,
      cracked: 0,
      cashoutMultiplier: null,
      payout: 0,
      phase: "betting",
    }),

  applyResolved: (res) =>
    set((s) => ({
      balance: res.balance,
      cashoutMultiplier: res.outcome === "win" ? res.cashoutMultiplier : null,
      payout: res.payout,
      phase: res.outcome === "win" ? "win" : "bust",
      history: pushHistory(s.history, {
        multiplier: res.cashoutMultiplier || s.multiplier,
        roundId: s.roundId,
        ts: Date.now(),
      }),
    })),

  applyWsEvent: (ev) => {
    const s = get();
    switch (ev.type) {
      case "round:state":
        if (ev.phase === "live") set({ phase: "live", multiplier: 1, cracked: 0 });
        return;
      case "tick":
        if (s.phase === "live") {
          set({ multiplier: ev.multiplier });
          // Auto-cashout trigger: only set the pending flag — actual cashout
          // is dispatched by GameView via a subscription. Keeps async out of
          // the reducer.
          const ap = s.autoplay;
          if (
            ap.active &&
            !ap.paused &&
            !ap.cashoutInFlight &&
            !ap.pendingAutoCashout &&
            s.autoplayConfig.autoCashoutTarget > 0 &&
            ev.multiplier >= s.autoplayConfig.autoCashoutTarget
          ) {
            set((prev) => ({
              autoplay: { ...prev.autoplay, pendingAutoCashout: true },
            }));
          }
        }
        return;
      case "crack":
        playLockCrack();
        if (ev.index < 4) {
          set({
            cracked: Math.max(s.cracked, ev.index + 1),
            crackPulseKey: s.crackPulseKey + 1,
          });
        } else {
          set({ cracked: 5, crackPulseKey: s.crackPulseKey + 1 });
        }
        return;
      case "resolved":
        set((prev) => ({
          phase: ev.outcome === "win" ? "win" : "bust",
          cashoutMultiplier: ev.outcome === "win" ? ev.multiplier : null,
          payout: ev.payout,
          multiplier: ev.multiplier,
          cracked: ev.outcome === "bust" ? 5 : prev.cracked,
          history: pushHistory(prev.history, {
            multiplier: ev.multiplier,
            roundId: prev.roundId,
            ts: Date.now(),
          }),
        }));
        return;
      case "error":
        // eslint-disable-next-line no-console
        console.warn("ws error", ev.message);
        return;
    }
  },

  setBet: (v) => set({ betAmount: v }),
  setPhase: (p) => set({ phase: p }),
  reset: () =>
    set({
      phase: "betting",
      roundId: null,
      multiplier: 1,
      cracked: 0,
      cashoutMultiplier: null,
      payout: 0,
    }),
  clearSession: () =>
    set({
      sessionId: null,
      token: null,
      serverSeedHash: null,
      clientSeed: null,
      roundId: null,
      roundNonce: null,
      phase: "idle",
      multiplier: 1,
      cracked: 0,
      cashoutMultiplier: null,
      payout: 0,
    }),
  refillDemoBalance: async (gameSlug) => {
    const s = get();
    try {
      const res = await rgs.launch({
        operatorId: s.operatorId,
        playerId: `demo_${Math.random().toString(36).slice(2, 8)}`,
        currency: s.currency,
        locale: s.locale,
        gameSlug,
      });
      set({
        sessionId: res.sessionId,
        token: res.token,
        currency: res.currency,
        gameSlug: res.gameSlug,
        balance: res.balance,
        serverSeedHash: res.serverSeedHash,
        clientSeed: res.clientSeed,
        phase: "betting",
        roundId: null,
        roundNonce: null,
        multiplier: 1,
        cracked: 0,
        cashoutMultiplier: null,
        payout: 0,
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("refillDemoBalance failed", err);
    }
  },
  setOperator: (operatorId, locale) => set({ operatorId, locale }),
  setBalance: (n) => set({ balance: n }),
  setDemoMode: (on) => set({ demoMode: on }),

  setMuted: (v) => {
    safeSetBool(PERSIST_KEY_MUTED, v);
    set({ muted: v });
  },
  setHideBalance: (v) => {
    safeSetBool(PERSIST_KEY_HIDE_BALANCE, v);
    set({ hideBalance: v });
  },
  setFairnessOverlay: (v) => {
    safeSetBool(PERSIST_KEY_FAIRNESS_OVERLAY, v);
    set({ fairnessOverlay: v });
  },

  setAutoplayConfig: (patch) => {
    const next = { ...get().autoplayConfig, ...patch };
    safeSetJson(PERSIST_KEY_AUTOPLAY, next);
    set({ autoplayConfig: next });
  },

  startAutoplay: () => {
    const s = get();
    const cfg = s.autoplayConfig;
    if (s.autoplay.active) return { ok: false, reason: "Already running" };
    if (cfg.autoCashoutTarget < 1.01 || cfg.autoCashoutTarget > 1000) {
      return { ok: false, reason: "Auto-cashout target must be between 1.01 and 1000" };
    }
    if (cfg.numberOfBets < 0 || cfg.numberOfBets > 1000) {
      return { ok: false, reason: "Number of bets must be 0..1000 (0 = infinite)" };
    }
    if (cfg.baseBet <= 0 || !Number.isFinite(cfg.baseBet)) {
      return { ok: false, reason: "Base bet must be positive" };
    }
    if (cfg.baseBet > s.balance) {
      return { ok: false, reason: "Base bet exceeds current balance" };
    }
    set({
      betAmount: cfg.baseBet,
      autoplay: {
        ...DEFAULT_AUTOPLAY_RUNTIME,
        active: true,
        remaining: cfg.numberOfBets === 0 ? Number.POSITIVE_INFINITY : cfg.numberOfBets,
        currentBet: cfg.baseBet,
        startedAt: Date.now(),
      },
    });
    return { ok: true };
  },

  stopAutoplay: (reason) =>
    set((s) => ({
      autoplay: { ...s.autoplay, active: false, paused: false, stopReason: reason ?? null },
    })),

  pauseAutoplay: () => set((s) => ({ autoplay: { ...s.autoplay, paused: true } })),
  resumeAutoplay: () => set((s) => ({ autoplay: { ...s.autoplay, paused: false } })),

  setCashoutInFlight: (v) =>
    set((s) => ({ autoplay: { ...s.autoplay, cashoutInFlight: v } })),
  acknowledgeAutoCashout: () =>
    set((s) => ({ autoplay: { ...s.autoplay, pendingAutoCashout: false } })),

  resolveAutoplay: (outcome, payoutDelta) => {
    const s = get();
    const ap = s.autoplay;
    const cfg = s.autoplayConfig;
    if (!ap.active) return null;

    const cumulativeProfit = ap.cumulativeProfit + payoutDelta;
    const remaining = ap.remaining === Number.POSITIVE_INFINITY ? ap.remaining : ap.remaining - 1;

    // Stop conditions ordered: limits → out of rounds → balance.
    if (cfg.stopOnProfit !== null && cumulativeProfit >= cfg.stopOnProfit) {
      set({
        autoplay: { ...ap, active: false, cumulativeProfit, remaining, stopReason: "Profit target hit" },
      });
      return null;
    }
    if (cfg.stopOnLoss !== null && -cumulativeProfit >= cfg.stopOnLoss) {
      set({
        autoplay: { ...ap, active: false, cumulativeProfit, remaining, stopReason: "Loss limit hit" },
      });
      return null;
    }
    if (remaining <= 0) {
      set({
        autoplay: { ...ap, active: false, cumulativeProfit, remaining: 0, stopReason: "Loop complete" },
      });
      return null;
    }

    // Apply the strategy step.
    const step = applyStrategy(cfg.strategy, outcome, {
      baseBet: cfg.baseBet,
      currentBet: ap.currentBet,
      fibIndex: ap.fibIndex,
      unitCount: ap.unitCount,
      onWinPct: cfg.onWinPct,
      onLossPct: cfg.onLossPct,
    });
    const nextBet = Math.max(0, step.nextBet);

    // Balance pre-check on the proposed next bet.
    if (nextBet <= 0 || !Number.isFinite(nextBet)) {
      set({
        autoplay: {
          ...ap,
          active: false,
          cumulativeProfit,
          remaining,
          stopReason: "Computed next bet invalid",
        },
      });
      return null;
    }
    if (nextBet > s.balance) {
      set({
        autoplay: {
          ...ap,
          active: false,
          cumulativeProfit,
          remaining,
          stopReason: "Insufficient balance for next bet",
        },
      });
      return null;
    }

    set({
      autoplay: {
        ...ap,
        cumulativeProfit,
        remaining,
        currentBet: nextBet,
        fibIndex: step.fibIndex,
        unitCount: step.unitCount,
        cashoutInFlight: false,
        pendingAutoCashout: false,
      },
      betAmount: nextBet,
    });
    return nextBet;
  },
}));
